import { useState, useCallback } from 'react';
import { Avatar, Button, Drawer } from 'antd';
import {
	AppstoreAddOutlined,
	MenuOutlined,
	SwapOutlined,
} from '@ant-design/icons';
import LanguageDropdown from './LanguageDropdown';
import LogoutImage from '../../assets/images/logout-red-icon.svg';
import { useTranslation } from 'react-i18next';
import { useBatchId } from '../../hooks/useBatchId';
import { ROOT, SELECT_MODULE } from '../../shared/Routes';

interface MobileHeaderProps {
	logout?: () => void | Promise<void>;
	username?: string;
	profileImage?: string;
	showHome?: boolean;
	showSwitchRole?: boolean;
}

export const MobileHeader = ({
	logout,
	username,
	profileImage,
	showHome,
	showSwitchRole,
}: MobileHeaderProps) => {
	const { t } = useTranslation();
	const [drawerVisible, setDrawerVisible] = useState(false);
	const handleDrawerOpen = useCallback(() => {
		setDrawerVisible(true);
	}, []);

	const handleDrawerClose = useCallback(() => {
		setDrawerVisible(false);
	}, []);
	const handleLogoutClick = useCallback(() => {
		if (logout) {
			void logout();
		}
	}, [logout]);

	const { navigateToRoute } = useBatchId();
	const logoutText = t('logout');
	const selectModule = t('selectModule');
	const switchRole = t('switchRole');
	const LogoutIcon = () => (
		<>
			<img src={LogoutImage} alt="Logout Icon" />
		</>
	);

	const handleSwitchRoleClick = useCallback(() => {
		navigateToRoute(ROOT);
	}, [navigateToRoute]);

	const handleHomeClick = useCallback(() => {
		sessionStorage.removeItem('SELECTED_BATCH');
		navigateToRoute(SELECT_MODULE);
	}, [navigateToRoute]);

	return (
		<>
			<div className="md:hidden">
				<Button
					type="text"
					icon={<MenuOutlined />}
					onClick={handleDrawerOpen}
				/>
			</div>

			<Drawer
				title="Menu"
				placement="right"
				onClose={handleDrawerClose}
				open={drawerVisible}
				closable={true}
				width="75vw"
			>
				<div className="flex flex-col items-start mt-4 space-y-6">
					{showSwitchRole && (
						<div
							onClick={handleSwitchRoleClick}
							className="flex items-center gap-x-2 px-3 py-2 cursor-pointer hover:bg-gray-100 rounded-md"
						>
							<SwapOutlined />
							<span className="font-sans font-medium text-sm text-gray-700">
								{switchRole}
							</span>
						</div>
					)}

					{/* Showing Home button to Admins, Approvers and ZCs */}
					{showHome && (
						<div
							className="flex items-center gap-x-2 py-1.5 px-3 border border-solid border-gray-200 rounded-lg cursor-pointer"
							onClick={handleHomeClick}
						>
							<AppstoreAddOutlined size={30} />
							<span className="font-sans font-medium text-gray-700 text-sm">
								{selectModule}
							</span>
						</div>
					)}

					{/* Avatar & Username in One Line */}
					<div className="flex items-center space-x-3">
						<Avatar
							src={profileImage}
							size={40}
							className="border border-gray-300 shadow-sm"
						/>
						<span className="font-sans font-medium text-gray-700 text-base">
							{username}
						</span>
					</div>

					{/*  Left-Aligned Menu Options */}
					<div className="flex flex-col items-start space-y-4">
						<LanguageDropdown className="w-28 text-center" />

						{/* Logout Button */}
						<Button
							type="link"
							shape="round"
							onClick={handleLogoutClick}
							size="large"
							className="text-red hover:!text-red font-sans font-medium flex !justify-start !p-0"
						>
							<LogoutIcon />
							<span>{logoutText}</span>
						</Button>
					</div>
				</div>
			</Drawer>
		</>
	);
};
